import { Component, useEffect, useMemo, useRef, useState } from "react";
import { provenanceLabel, type ChartArtifacts, type ChartManifest } from "../../../shared/charts/manifest.ts";
import type { ComponentProps } from "./index";
import type { TeachingModel } from "../../../shared/charts/models.ts";
import { chartDownloads, drawablePlot, editedModel, modelControls, modelInputs, PLOT_CONFIG, readChartResult, readManifest, statisticsTable, withModel, type DrawnPlot } from "./chartView";
import { useWorkspaceJson, workspaceUrl } from "./useWorkspaceJson";

type Plotly = typeof import("plotly.js-dist-min").default;

let loading: Promise<Plotly> | null = null;

/** The bundled Plotly, loaded the first time a chart is drawn; a failed load is tried again next time. */
function plotly(): Promise<Plotly> {
	loading ??= import("plotly.js-dist-min")
		.then((module) => module.default)
		.catch((err: unknown) => {
			loading = null;
			throw err;
		});
	return loading;
}

/**
 * Renders a `show_chart` call: the chart it saved, fetched through the workspace route and drawn
 * with the bundled Plotly. Hover or tap inspects values, the legend toggles series, the modebar
 * zooms and pans, and 重置 draws the saved chart again. Nothing here writes or answers
 * the agent; reopening the chat draws the saved chart as it was first shown.
 */
export function Chart({ sessionId, result }: ComponentProps): React.ReactNode {
	const read = readChartResult(result);
	if (read.state === "running") {
		return (
			<div className="component chart-component">
				<p className="hint">绘制中…</p>
			</div>
		);
	}
	if (read.state === "failed") return <div className="component error">图表绘制失败：{read.message}</div>;
	return <SavedChart sessionId={sessionId} chart={read.chart} artifacts={read.artifacts} />;
}

function SavedChart({ sessionId, chart, artifacts }: { sessionId: string; chart: string; artifacts: ChartArtifacts }) {
	const { data, error, loading } = useWorkspaceJson<unknown>(sessionId, chart);
	const saved = useMemo(() => (data === null ? null : readManifest(data)), [data]);
	if (error) return <div className="component error">{error}</div>;
	if (loading || !saved) {
		return (
			<div className="component chart-component">
				<p className="hint">加载中…</p>
			</div>
		);
	}
	if (!saved.ok) return <div className="component error">{chart}: {saved.message}</div>;
	return (
		<ChartBoundary>
			<ChartView sessionId={sessionId} manifest={saved.manifest} artifacts={artifacts} />
		</ChartBoundary>
	);
}

/** A chart file that renders badly shows its error here instead of taking the chat down with it. */
class ChartBoundary extends Component<{ children: React.ReactNode }, { error: string | null }> {
	state = { error: null as string | null };

	static getDerivedStateFromError(err: unknown) {
		return { error: err instanceof Error ? err.message : String(err) };
	}

	render() {
		if (this.state.error) return <div className="component error">图表无法显示：{this.state.error}</div>;
		return this.props.children;
	}
}

function ChartView({ sessionId, manifest, artifacts }: { sessionId: string; manifest: ChartManifest; artifacts: ChartArtifacts }) {
	// Each reset draws a new copy of the saved plot, which Plotly has not touched.
	const [revision, setRevision] = useState(0);
	// A model chart redraws its curve and samples from the edited inputs; null is the saved model.
	const saved = manifest.model;
	const [inputs, setInputs] = useState(() => (saved ? modelInputs(saved) : {}));
	const [model, setModel] = useState<TeachingModel | null>(null);
	const [invalid, setInvalid] = useState<string | null>(null);
	const plot = useMemo(() => {
		try {
			const data = model ? withModel(manifest.plot.data, model) : manifest.plot.data;
			return { ok: true as const, plot: drawablePlot({ data, layout: manifest.plot.layout }) };
		} catch (err) {
			return { ok: false as const, message: err instanceof Error ? err.message : String(err) };
		}
	}, [manifest, revision, model]);
	const edit = (name: string, value: string) => {
		if (!saved) return;
		const next = { ...inputs, [name]: value };
		setInputs(next);
		const result = editedModel(saved, next);
		if (result.ok) {
			setModel(result.model);
			setInvalid(null);
		} else {
			setInvalid(result.message);
		}
	};
	const reset = () => {
		setRevision((r) => r + 1);
		if (saved) setInputs(modelInputs(saved));
		setModel(null);
		setInvalid(null);
	};
	const statistics = statisticsTable(manifest.statistics ?? []);
	return (
		<div className="component chart-component">
			<div className="chart-title">{manifest.title}</div>
			<p className="chart-provenance">{provenanceLabel(manifest.provenance)}</p>
			{manifest.warnings.length > 0 && (
				<ul className="chart-warnings">
					{manifest.warnings.map((warning) => (
						<li key={warning}>{warning}</li>
					))}
				</ul>
			)}
			{saved && (
				<div className="chart-model">
					{modelControls(saved).map((c) => (
						<label key={c.name} className="chart-model-field">
							<span>{c.label}</span>
							<input type="text" inputMode="decimal" aria-label={c.label} value={inputs[c.name] ?? ""} onChange={(e) => edit(c.name, e.target.value)} />
						</label>
					))}
				</div>
			)}
			{invalid && (
				<p className="error" role="alert">
					{invalid}
				</p>
			)}
			<div className="chart-actions">
				<button type="button" onClick={reset}>
					重置
				</button>
			</div>
			{plot.ok ? <PlotArea plot={plot.plot} label={manifest.title} /> : <p className="error">{plot.message}</p>}
			{statistics.rows.length > 0 && (
				<details className="chart-details">
					<summary>统计</summary>
					<div className="table-wrap">
						<table className="data-table chart-statistics">
							<thead>
								<tr>
									{statistics.head.map((h) => (
										<th key={h}>{h}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{statistics.rows.map((row, i) => (
									<tr key={i}>
										{row.map((cell, j) => (
											<td key={j}>{cell}</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{statistics.notes.length > 0 && (
						<ul className="chart-notes">
							{statistics.notes.map((note) => (
								<li key={note}>{note}</li>
							))}
						</ul>
					)}
				</details>
			)}
			{manifest.explanation.length > 0 && (
				<details className="chart-details">
					<summary>计算说明</summary>
					<ul className="chart-notes">
						{manifest.explanation.map((line) => (
							<li key={line}>{line}</li>
						))}
					</ul>
				</details>
			)}
			<div className="chart-downloads">
				<span className="hint">下载</span>
				{chartDownloads(artifacts).map((d) => (
					<a key={d.label} href={workspaceUrl(sessionId, d.path)} download={d.path.slice(d.path.lastIndexOf("/") + 1)}>
						{d.label}
					</a>
				))}
			</div>
		</div>
	);
}

/** Draws one copy of the plot; a new copy draws afresh, and leaving purges Plotly's listeners. */
function PlotArea({ plot, label }: { plot: DrawnPlot; label: string }) {
	const root = useRef<HTMLDivElement>(null);
	const [failure, setFailure] = useState<string | null>(null);
	useEffect(() => {
		const element = root.current;
		if (!element) return;
		let gone = false;
		let drawn: Plotly | undefined;
		setFailure(null);
		plotly()
			.then((Plotly) => {
				if (gone) return;
				drawn = Plotly;
				return Plotly.newPlot(element, plot.data, plot.layout, { ...PLOT_CONFIG });
			})
			.catch((err: unknown) => {
				if (!gone) setFailure(`The chart could not be drawn: ${err instanceof Error ? err.message : String(err)}`);
			});
		return () => {
			gone = true;
			drawn?.purge(element);
		};
	}, [plot]);
	return (
		<>
			<div className="chart-plot" ref={root} role="figure" aria-label={label} />
			{failure && <p className="error">{failure}</p>}
		</>
	);
}
